import { useState } from 'react';
import {
  INVOICE_STATUSES,
  INVOICE_TARGETS,
  type InvoiceStatus,
  type InvoiceTarget,
  type Invoice,
  type SaveInvoiceRequest,
} from '@forecastify/shared';
import { Select, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { EditorDialog } from '../settings/editor-dialog';
import { DateField } from '../date-field';
import { Field } from '../card';
import { CommentComposer } from '../tasks/comment-composer';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ A számla szerkesztője — C57.                                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE TARGET PICKER IS LOCKED WHEN THE DIALOG WAS OPENED BY *Számlázás*.** `invBill` in the
 * mockup passes the source and the remaining amount already filled in, and the form then asks only
 * what it does not know — the external number, the date, whether to close the source afterwards.
 * Offering the picker there would invite somebody to bill the deliverable they were not looking at.
 *
 * ⚠️⚠️ **THE CURRENCY IS THE SOURCE'S AND IS NOT EDITABLE.** The mockup says it in words under the
 * field — *"A forrás bevételének pénzneme"* — and the server enforces it: an invoice in euros
 * against a forint line item makes *Kiszámlázva* a sum of two units.
 */
export interface InvoiceDraft {
  id: string | null;
  externalId: string;
  issuedOn: string;
  amount: string;
  /** ⚠️ Display only — the server takes the source's. */
  currencyCode: string;
  status: InvoiceStatus;
  /** `0210` · the note as HTML, `''` for none. The server sanitises it and derives the plain twin. */
  noteHtml: string;
  targetKind: InvoiceTarget;
  lineItemId: string;
  contractId: string;
  occurrenceStartsOn: string;
  ownerMemberId: string;
  /** ⚠️ Set by *Számlázás*: the target is fixed and the closing question is asked. */
  locked: boolean;
  closeSource: boolean;
  version: number | undefined;
}

export function draftOf(invoice: Invoice): InvoiceDraft {
  return {
    id: invoice.id,
    externalId: invoice.externalId,
    issuedOn: invoice.issuedOn,
    amount: invoice.amount,
    currencyCode: invoice.currencyCode,
    status: invoice.status,
    noteHtml: invoice.noteHtml,
    targetKind: invoice.targetKind,
    lineItemId: invoice.lineItemId ?? '',
    contractId: invoice.contractId ?? '',
    occurrenceStartsOn: invoice.occurrenceStartsOn ?? '',
    ownerMemberId: invoice.ownerMemberId,
    locked: false,
    closeSource: false,
    version: invoice.version,
  };
}

export function emptyDraft(
  currencyCode: string,
  ownerMemberId: string,
  preset?: Partial<InvoiceDraft>,
): InvoiceDraft {
  return {
    id: null,
    externalId: '',
    /* ⚠️ Today, because an invoice is recorded on the day it is issued. */
    issuedOn: new Date().toISOString().slice(0, 10),
    amount: '',
    currencyCode,
    status: 'issued',
    noteHtml: '',
    targetKind: 'line_item',
    lineItemId: '',
    contractId: '',
    occurrenceStartsOn: '',
    ownerMemberId,
    locked: false,
    closeSource: false,
    version: undefined,
    ...preset,
  };
}

const STATUS_KEY: Record<InvoiceStatus, 'draft' | 'issued' | 'paid' | 'void'> = {
  draft: 'draft',
  issued: 'issued',
  paid: 'paid',
  void: 'void',
};

export function InvoiceDialog(props: {
  readonly draft: InvoiceDraft;
  readonly lineItems: readonly { readonly id: string; readonly name: string }[];
  readonly contracts: readonly { readonly id: string; readonly code: string }[];
  readonly periods: readonly { readonly startsOn: string; readonly label: string }[];
  readonly members: readonly { readonly id: string; readonly name: string }[];
  /** ⚠️ What is left to bill on the picked source, already formatted. Empty when unknown. */
  readonly remainingLabel: string;
  readonly serverError: string | null;
  readonly onCancel: () => void;
  readonly onSave: (body: SaveInvoiceRequest, closeSource: boolean) => void;
  /**
   * ⚠️ ux-pass-2 · `'target'` asks ONLY what the invoice bills — opened from the detail's
   * *Forrás* row, since every other field there is edited in place. The rest of the draft rides
   * along unchanged, so the save body is still the whole record the `PUT` takes.
   */
  readonly focus?: 'target';
}) {
  const t = useT();
  const all = props.focus === undefined;
  const [draft, setDraft] = useState(props.draft);

  const patch = (change: Partial<InvoiceDraft>): void => {
    setDraft((current) => ({ ...current, ...change }));
  };

  const amount = Number(draft.amount.replace(/\s/g, '').replace(',', '.'));
  const valid =
    draft.externalId.trim() !== '' &&
    Number.isFinite(amount) &&
    amount > 0 &&
    draft.issuedOn !== '' &&
    draft.ownerMemberId !== '' &&
    (draft.targetKind !== 'line_item' || draft.lineItemId !== '') &&
    (draft.targetKind !== 'occurrence' ||
      (draft.contractId !== '' && draft.occurrenceStartsOn !== ''));

  const statusLabel = (status: InvoiceStatus): string => {
    /* ⚠️ One literal key per branch — a computed one is invisible to `i18n:check`. */
    const key = STATUS_KEY[status];
    if (key === 'draft') return t('invoice.status.draft');
    if (key === 'issued') return t('invoice.status.issued');
    if (key === 'paid') return t('invoice.status.paid');
    return t('invoice.status.void');
  };

  return (
    <EditorDialog
      open
      title={
        !all
          ? t('invoice.field.target')
          : draft.id !== null
            ? t('invoice.dialog.edit')
            : draft.locked
              ? t('invoice.dialog.bill')
              : t('invoice.dialog.new')
      }
      {...(all
        ? {
            description: draft.locked ? t('invoice.dialog.billHint') : t('invoice.dialog.hint'),
          }
        : {})}
      saveLabel={
        draft.id !== null
          ? t('common.save')
          : draft.locked
            ? t('invoice.action.issue')
            : t('common.create')
      }
      saveDisabled={!valid}
      onClose={props.onCancel}
      onSave={() => {
        const common = {
          externalId: draft.externalId.trim(),
          amount,
          issuedOn: draft.issuedOn,
          status: draft.status,
          noteHtml: draft.noteHtml,
          ownerMemberId: draft.ownerMemberId,
          ...(draft.version === undefined ? {} : { version: draft.version }),
        };

        /* ⚠️ Spread as ONE of two shapes — the discriminated union refuses a body naming both, the
           same rule `0134`'s check enforces in the table. */
        const body: SaveInvoiceRequest =
          draft.targetKind === 'occurrence'
            ? {
                targetKind: 'occurrence',
                contractId: draft.contractId,
                occurrenceStartsOn: draft.occurrenceStartsOn,
                ...common,
              }
            : { targetKind: 'line_item', lineItemId: draft.lineItemId, ...common };

        props.onSave(body, draft.closeSource);
      }}
    >
      {props.serverError === null ? null : (
        <p className="text-destructive text-[12px]">{props.serverError}</p>
      )}

      {!all ? null : (
        <>
          <Field label={t('invoice.field.externalId')}>
            <TextInput
              value={draft.externalId}
              autoFocus
              placeholder={t('invoice.field.externalIdPlaceholder')}
              onChange={(event) => {
                patch({ externalId: event.target.value });
              }}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('invoice.field.amount')}>
              <TextInput
                value={draft.amount}
                className="text-right tabular-nums"
                onChange={(event) => {
                  patch({ amount: event.target.value });
                }}
              />
            </Field>
            <Field label={t('invoice.field.currency')}>
              {/* ⚠️ Disabled rather than absent: a reader needs to know WHICH currency the figure they
              are typing is in, and a field that is not there answers that with nothing. */}
              <TextInput value={draft.currencyCode} disabled readOnly onChange={() => undefined} />
            </Field>
          </div>
          <p className="text-muted-foreground text-[11.5px]">{t('invoice.field.currencyNote')}</p>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('invoice.field.date')}>
              <DateField
                label={t('invoice.field.date')}
                value={draft.issuedOn}
                onChange={(value) => {
                  patch({ issuedOn: value });
                }}
              />
            </Field>
            <Field label={t('invoice.field.status')}>
              <Select
                value={draft.status}
                onChange={(event) => {
                  patch({ status: event.target.value as InvoiceStatus });
                }}
                options={INVOICE_STATUSES.map((status) => ({
                  value: status,
                  label: statusLabel(status),
                }))}
              />
            </Field>
          </div>
        </>
      )}

      {draft.locked ? null : (
        <Field label={t('invoice.field.target')}>
          <Select
            value={draft.targetKind}
            onChange={(event) => {
              /* ⚠️ The other arm is CLEARED — a body naming a line item while pointing at an
                 occurrence is what the table's check refuses, and the form must not build one. */
              patch({
                targetKind: event.target.value as InvoiceTarget,
                lineItemId: '',
                contractId: '',
                occurrenceStartsOn: '',
              });
            }}
            options={INVOICE_TARGETS.map((kind) => ({
              value: kind,
              label:
                kind === 'line_item'
                  ? t('invoice.target.lineItem')
                  : t('invoice.target.occurrence'),
            }))}
          />
        </Field>
      )}

      {draft.locked || draft.targetKind !== 'line_item' ? null : (
        <Field label={t('invoice.target.lineItem')}>
          {/**
           * ┌─────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ THE THREE PICKERS TAKE A SEARCH — round 5 item 26.             │
           * └─────────────────────────────────────────────────────┘
           *
           * *"A sortétel, szerződés és időszak legördülők kereshetőek legyenek."* All three list a
           * whole workspace's worth of rows: every deliverable of every contract, every contract,
           * and a retainer's twenty-four periods.
           *
           * ⚠️⚠️ **NO NEW COMPONENT — `Select` HAS HAD `searchable` SINCE ROUND 4 ITEM 21**, where
           * it was built for the client and PM pickers for this exact reason and draws the line
           * at ten options. Writing a second search-and-select would have been the fifth control
           * on this screen a reader has to learn separately.
           */}
          <Select
            searchable
            searchPlaceholder={t('invoice.target.lineItem')}
            value={draft.lineItemId}
            onChange={(event) => {
              patch({ lineItemId: event.target.value });
            }}
            options={[
              { value: '', label: t('invoice.field.pickLineItem') },
              ...props.lineItems.map((one) => ({ value: one.id, label: one.name })),
            ]}
          />
        </Field>
      )}

      {draft.locked || draft.targetKind !== 'occurrence' ? null : (
        <>
          <Field label={t('invoice.field.contract')}>
            <Select
              /* ⚠️ Item 26 — every contract of the workspace, so the same reason as above. */
              searchable
              searchPlaceholder={t('invoice.field.contract')}
              value={draft.contractId}
              onChange={(event) => {
                patch({ contractId: event.target.value, occurrenceStartsOn: '' });
              }}
              options={[
                { value: '', label: t('invoice.field.pickContract') },
                ...props.contracts.map((one) => ({ value: one.id, label: one.code })),
              ]}
            />
          </Field>
          <Field label={t('invoice.target.occurrence')}>
            <Select
              /* ⚠️ Item 26 — a monthly retainer running two years is twenty-four rows, and the
                 reader is looking for one date. */
              searchable
              searchPlaceholder={t('invoice.target.occurrence')}
              value={draft.occurrenceStartsOn}
              onChange={(event) => {
                patch({ occurrenceStartsOn: event.target.value });
              }}
              options={[
                { value: '', label: t('invoice.field.pickPeriod') },
                ...props.periods.map((one) => ({ value: one.startsOn, label: one.label })),
              ]}
            />
          </Field>
        </>
      )}

      {/**
       * ⚠️⚠️ **WHAT IS LEFT TO BILL, SAID BEFORE THE FIGURE IS TYPED.** The server refuses an
       * invoice larger than this, and a refusal arriving after somebody has filled the whole form
       * is the worst place to learn a number that was knowable all along.
       */}
      {props.remainingLabel === '' ? null : (
        <p className="text-muted-foreground text-[11.5px]">
          {t('invoice.field.remainingHint', { amount: props.remainingLabel })}
        </p>
      )}

      {!all ? null : (
        <>
          {/* ⚠️ NOT `Field`: that is a `<label>`, and a click anywhere inside a label is forwarded to
          its first labelable descendant — here the toolbar's first button, so every click into the
          text would have toggled bold. The same caption, on a `div`. */}
          <div className="flex flex-col gap-1.5">
            <span className="text-[12px] font-medium">{t('invoice.field.note')}</span>
            {/**
             * ⚠️⚠️ **THE COMMENTS' EDITOR, AS A FIELD — ux-pass-2, 14 Oct.** *"Megjegyzés field should be
             * a rich text editor like the comments are."* `CommentComposer` rather than a second
             * editor, for the reason the task description gives: one toolbar, one paste path, one
             * allow-list. `onChange` makes it a field of this form — no Send button, and the dialog's
             * own Save writes it. No `people` and no files: an `@` here would notify nobody, and an
             * invoice's files are the section beside the note on the detail screen.
             */}
            <CommentComposer
              initialHtml={draft.noteHtml}
              allowFiles={false}
              footer={false}
              placeholder={t('invoice.field.notePlaceholder')}
              label={t('invoice.field.note')}
              onSubmit={() => undefined}
              onChange={(html) => {
                patch({ noteHtml: html });
              }}
            />
          </div>

          <Field label={t('invoice.field.owner')}>
            <Select
              value={draft.ownerMemberId}
              onChange={(event) => {
                patch({ ownerMemberId: event.target.value });
              }}
              /* ⚠️ Nobody until somebody is chosen, and searchable: the list is the whole
             workspace, and the first name in it was being pre-selected as if it were a default. */
              searchable
              searchPlaceholder={t('contract.owner.search')}
              options={[
                { value: '', label: t('common.pleaseChoose') },
                ...props.members.map((one) => ({ value: one.id, label: one.name })),
              ]}
            />
          </Field>

          {/**
           * ⚠️ **The closing question, and only when *Számlázás* asked it.** `invDialogOpen`'s
           * `askClose` — billing the last of a deliverable is usually the moment it is finished, and
           * the mockup offers to close it right there rather than sending somebody to a second screen.
           */}
          {!draft.locked || draft.id !== null ? null : (
            <label className="flex items-start gap-2 text-[12.5px]">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={draft.closeSource}
                onChange={(event) => {
                  patch({ closeSource: event.target.checked });
                }}
              />
              <span>
                <span className="block font-medium">
                  {draft.targetKind === 'occurrence'
                    ? t('invoice.close.occurrence')
                    : t('invoice.close.lineItem')}
                </span>
                <span className="text-muted-foreground block text-[11.5px]">
                  {draft.targetKind === 'occurrence'
                    ? t('invoice.close.occurrenceHint')
                    : t('invoice.close.lineItemHint')}
                </span>
              </span>
            </label>
          )}
        </>
      )}
    </EditorDialog>
  );
}
